import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  model,
  OnChanges,
  OnInit,
  Output,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  MbsSize,
  ModalService,
  SafePipe,
  SwitcherComponent,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { BetaFeatureRequestComponent } from 'mbs-ui-shared@components';
import { GuidEmpty } from 'mbs-ui-shared@constants';
import { ComputerBackupFacade } from 'mbs-ui-shared@facades/computer.backup.facade';
import { RemoteDeployDataFacade } from 'mbs-ui-shared@facades/remote-deploy-data.facade';
import { StorageType } from 'mbs-ui-shared@models';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { BetaFeatureBuildName } from 'mbs-ui-shared@services/beta-request.service';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { BehaviorSubject, noop, Observable, shareReplay, switchMap, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AgentVersionForNBFHybrid } from 'wizards/helpers/bases/base-for-steps-helper';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { DestinationPlusRetentionStepValue } from 'wizards/models/combined/destination-plus-retention-model';
import { RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { WizardCombinedDataManagerClass } from 'wizards/remote-management-wizards/strategy/wizard-combined-data-manager.class';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';
import { RetentionPolicyControlPanelComponent } from '../../retention-policy-step/NBF/control-panel/retention-policy-control-panel.component';
import { RetentionPolicyStepLogic } from '../../retention-policy-step/NBF/retention-policy-step.logic';
import { WhereBackupStepStorageListModalComponent } from '../../where-backup-step/storage-list-modal/where-backup-step-storage-list-modal.component';
import { WhereBackupStepLogic } from '../../where-backup-step/where-backup-step.logic';
import { DestinationPlusRetentionStepAddPlanModalComponent } from './add-destination-modal/add-destination-modal.component';

const DestinationPlusRetentionStepComponentValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationPlusRetentionStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-plus-retention-step',
  templateUrl: './destination-plus-retention-step.component.html',
  styleUrls: ['./destination-plus-retention-step.component.scss'],
  providers: [DestinationPlusRetentionStepComponentValueAccessor],
  imports: [
    CommonModule,
    NgbTooltipModule,
    ReactiveFormsModule,
    ButtonComponent,
    I18NextPipe,
    StorageTypeIconPipe,
    TextEllipsisComponent,
    SwitcherComponent,
    CardComponent,
    CardGroupComponent,
    RetentionPolicyControlPanelComponent,
    SafePipe,
    BetaFeatureRequestComponent
  ]
})
export class DestinationPlusRetentionStepComponent extends StepBase<DestinationPlusRetentionStepValue> implements OnInit, OnChanges {
  @Input() Storages: Storages = null;
  @Input() scheduleTitle: string;
  @Input() advancedOptionsTitle = '';
  @Input() advancedOptionsValid = false;
  @Input() advancedOptionsAvailable = false;
  @Input() canAddStorageAccount = true;

  @Output() changeStorage = new EventEmitter();
  @Output() advancedOptionsOpened = new EventEmitter<boolean>();

  @ViewChild('advancedOptions', { static: true, read: TemplateRef }) advancedOptions: TemplateRef<HTMLElement>;

  public ability = inject(AbilityService);
  public backupFacade = inject(ComputerBackupFacade);
  public mainService = inject(RemoteManagementWizardsService);
  private injector = inject(Injector);
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);
  private cdr = inject(ChangeDetectorRef);

  public readonly elementSelectors = {
    id: {
      advancedOptionsButton: 'DpR-advancedOptionsButton'
    }
  };
  public readonly betaFeatureBtnData = {
    backupBuilds: BetaFeatureBuildName.BackupBuilds,
    agentVersionForNBFHybrid: AgentVersionForNBFHybrid,
    nbfHybrid: 'nbf-hybrid'
  };
  public readonly PlanTypes = PlanTypes;
  public readonly StorageType = StorageType;
  protected readonly MbsSize = MbsSize;

  public isHybrid = model<boolean>(false);
  public minPeriod: number = null;
  public whereBackupStepLogic: WhereBackupStepLogic;
  public retentionPolicyStepLogic: RetentionPolicyStepLogic;
  public stepForm: FormGroup<{
    whereBackupStep: FormGroup<{ IsHybridBackup: FormControl<boolean>; ConnectionID: FormControl<string>; HybridID: FormControl<string> }>;
    retentionPolicyStep: FormGroup;
    valid: FormControl<boolean>;
  }>;

  private formWrited = new BehaviorSubject<boolean>(false);
  private disableSaveAdvancedOptions = new BehaviorSubject<boolean>(false);

  set retentionValue(value: RetentionPolicyStepValue) {
    this.value = { ...this.value, retentionPolicyStep: value };
  }
  get retentionValue(): RetentionPolicyStepValue {
    return this.value.retentionPolicyStep;
  }

  constructor() {
    super();

    this.whereBackupStepLogic = new WhereBackupStepLogic(this.mainService, this.backupFacade, this.i18nPipe, this);
    this.retentionPolicyStepLogic = new RetentionPolicyStepLogic(this.stepService, this.modalService, this.i18nPipe, this);

    this.retentionPolicyStepLogic.initIsHybrid(this.isHybrid);
  }

  ngOnInit() {
    this.whereBackupStepLogic.init();

    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    this.stepForm = new UntypedFormGroup({
      whereBackupStep: this.whereBackupStepLogic.getForm(),
      retentionPolicyStep: this.retentionPolicyStepLogic.getForm()
    });

    this.initFormEvents();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.Storages && changes.Storages.currentValue && !this.whereBackupStepLogic.MixStorages.length) {
      this.whereBackupStepLogic.setStorages(changes.Storages.currentValue);

      this.stepForm && this.stepForm.updateValueAndValidity();

      if (this.stepForm && !this.whereBackupStepLogic.existAllStorages) {
        this.stepForm.controls.whereBackupStep.controls.IsHybridBackup.reset(false);
      }
    }

    if (changes.advancedOptionsValid) {
      this.disableSaveAdvancedOptions.next(!changes.advancedOptionsValid.currentValue);
    }
  }

  onStepFormChange(value: DestinationPlusRetentionStepValue): void {
    this.whereBackupStepLogic.updateSelectedItems(value.whereBackupStep.HybridID, value.whereBackupStep.ConnectionID);

    this.value = { ...this.getNewValue(), retentionPolicyStep: this.retentionPolicyStepLogic.getUpdatedValue(value.retentionPolicyStep) };
  }

  updateForm(value: DestinationPlusRetentionStepValue) {
    const currentValue = this.getNewValue();
    const isWhereBackupTheSame = WizardCombinedDataManagerClass.isWhereBackupTheSame(currentValue.whereBackupStep, value.whereBackupStep);
    const isRetentionTheSame = WizardCombinedDataManagerClass.isRetentionTheSame(
      currentValue.retentionPolicyStep,
      value.retentionPolicyStep
    );

    if (isWhereBackupTheSame && isRetentionTheSame) return;

    this.formWrited.next(true);

    this.stepForm.controls.whereBackupStep.reset(value.whereBackupStep);
    this.retentionPolicyStepLogic.updateForm(value.retentionPolicyStep);

    const connectionId = this.stepForm.controls.whereBackupStep.controls.ConnectionID.value;
    const hybridId = this.stepForm.controls.whereBackupStep.controls.HybridID.value;

    if (connectionId && connectionId !== GuidEmpty) {
      this.changeSelectedHandler([connectionId]);
    }
    if (hybridId && hybridId !== GuidEmpty) {
      this.changeSelectedHandler([hybridId], true);
    }

    if (value.whereBackupStep.IsHybridBackup) this.isHybrid.set(true);

    this.whereBackupStepLogic.LocalStorages.forEach(
      (storage: StorageConnection) => (storage.disabled = this.whereBackupStepLogic.needHideHybridControls)
    );
  }

  forceValid(): void {
    this.stepForm.markAllAsTouched();
    this.stepForm.updateValueAndValidity();

    if (this.isNBF) {
      this.stepForm.controls.retentionPolicyStep.get('deleteVersionsOlderThanCount').markAsDirty();
      this.stepForm.controls.retentionPolicyStep.get('deleteVersionsOlderThanCount').markAsTouched();
      this.stepForm.controls.retentionPolicyStep.get('deleteVersionsOlderThanCount').updateValueAndValidity();
    }
  }

  detectChanges(): void {
    this.cdr.detectChanges();
  }

  beforeIsHybridBackupChangeHandler(event: { stop: () => void }): void {
    if (this.isHybrid()) {
      return;
    }

    const { selected, local, mix } = this.getAvailableStorages();
    const selectedIsLocal = [StorageType.FTP, StorageType.SFTP, StorageType.LocalFS].includes(selected?.StorageType);
    const openModal = () =>
      this.modalService
        .open(
          {
            header: { title: this.i18nPipe.transform('wizards:destinationPlusRetention.addNewDestination.title.modal') },
            footer: {
              okButton: { text: this.i18nPipe.transform('wizards:destinationPlusRetention.addNewDestination.button.ok') },
              cancelButton: { text: this.i18nPipe.transform('shared:buttons.close') }
            }
          },
          this.i18nPipe.transform('wizards:destinationPlusRetention.addNewDestination.description.modal')
        )
        .then((result) => {
          if (result) {
            this.showAddNewDestinationModal();
          }
        });

    if (selectedIsLocal) {
      if (mix.length) {
        // Normal case. Will handle at isHybridBackupChangeHandler
        return;
      }
    } else if (!local.length) {
      event.stop();

      return void openModal();
    }
  }

  isHybridBackupChangeHandler(event): void {
    const final = () => {
      this.isHybrid.set(!!event);
      this.stepForm.controls.whereBackupStep.controls.IsHybridBackup.setValue(!!event);
    };

    if (event) {
      const { selected, mix } = this.getAvailableStorages();

      if (selected?.StorageType === StorageType.LocalFS && mix.length) {
        return void this.modalService
          .open(
            {
              header: { title: this.i18nPipe.transform('wizards:destinationPlusRetention.moveDestinationToLocal.title') },
              footer: { cancelButton: { show: false } }
            },
            `
            ${this.i18nPipe.transform('wizards:destinationPlusRetention.moveDestinationToLocal.text1')}
            </br></br>
            ${this.i18nPipe.transform('wizards:destinationPlusRetention.moveDestinationToLocal.text2')}
            `
          )
          .finally(() => {
            this.changeSelectedHandler([], false);
            this.changeSelectedHandler([selected.ID], true);

            final();
          });
      }
    }

    final();
  }

  handleShowAdvancedOptions(): void {
    (this.confirmedAsEnabled ? Promise.resolve(true) : this.showEnableStepModal(this.modalService)).then((confirm) => {
      if (!confirm) return;

      this.confirmedAsEnabled = true;
      this.advancedOptionsOpened.emit(true);

      this.modalService
        .open(
          {
            modalDialogClass: 'mbs-advanced-options-step-modal',
            header: { title: this.advancedOptionsTitle, showCloseCross: false },
            footer: {
              okButton: { disabled$: this.disableSaveAdvancedOptions },
              cancelButton: { show: false }
            },
            keyboard: false
          },
          this.advancedOptions
        )
        .finally(() => {
          this.advancedOptionsOpened.emit(false);
        });
    });
  }

  handleAddDestination(id: string, hybridMode: boolean): void {
    let storages = hybridMode ? this.whereBackupStepLogic.LocalStorages : this.whereBackupStepLogic.MixStorages;

    if (this.isHybrid()) {
      const anotherStorage = hybridMode
        ? this.whereBackupStepLogic.selectedNotLocalStorage
        : this.whereBackupStepLogic.selectedLocalStorage;

      storages = storages.filter((item) => item.ID !== anotherStorage?.ID);
    }

    // Templates wizards run without a computer (hid is empty), so the modal hides in-place creation and no snapshot is needed
    const knownIds$ = this.mainService.hid ? this.whereBackupStepLogic.snapshotDestinationIds(this.injector).pipe(shareReplay(1)) : null;
    knownIds$?.pipe(untilDestroyed(this)).subscribe();

    this.modalService
      .openRef(WhereBackupStepStorageListModalComponent, {
        size: MbsSize.md,
        data: {
          hid: this.mainService.hid,
          isNBF: this.isNBF,
          canAddStorageAccount: this.canAddStorageAccount,
          isRDMode: this.isRDMode,
          isSQLPlan: this.isSQLPlan,
          storages: storages,
          selectedStorages: id ? [id] : null
        }
      })
      .result.then((result) => {
        if (result?.created) {
          if (this.isRDMode && result.accountId) {
            this.handleDestinationCreatedForRD(hybridMode, result.accountId);
          } else if (knownIds$) {
            this.handleDestinationCreated(hybridMode, knownIds$);
          }
        } else if (result && !result.created) {
          this.changeSelectedHandler(result, hybridMode);
        }
      });
  }

  private handleDestinationCreated(hybridMode: boolean, knownIds$: Observable<Set<string>>): void {
    knownIds$
      .pipe(
        switchMap((knownIds) => this.whereBackupStepLogic.applyCreatedDestination(this.injector, knownIds)),
        untilDestroyed(this)
      )
      .subscribe((createdId) => createdId && this.changeSelectedHandler([createdId], hybridMode));
  }

  private handleDestinationCreatedForRD(hybridMode: boolean, accountId: string): void {
    // The facade is resolved lazily: its dependency chain is wired only in mbs-ui, and RD mode is unreachable elsewhere
    this.whereBackupStepLogic
      .applyCreatedDestinationForRD(this.injector.get(RemoteDeployDataFacade), accountId)
      .pipe(untilDestroyed(this))
      .subscribe((createdId) => createdId && this.changeSelectedHandler([createdId], hybridMode));
  }

  handleDeleteDestination(hybridMode): void {
    this.changeSelectedHandler([], hybridMode);
  }

  retentionUseDefaultSettingsChangeHandler(): void {
    this.retentionPolicyStepLogic.retentionUseDefaultSettingsChangeHandler();
  }

  retentionChangeHandler(): void {
    this.retentionPolicyStepLogic.retentionChangeHandler();
  }

  retentionHybridChangeHandler(): void {
    if (!this.isHybrid() || !this.isNBF) return;

    this.retentionPolicyStepLogic.retentionHybridChangeHandler();
  }

  beforeGFSEnabledChangeHandler(event: { currentValue: boolean; nextValue: boolean; stop: () => void }): void {
    if (!event.nextValue) {
      return;
    }

    const isSimple = this.retentionPolicyStepLogic.schedule.isSimpleMode;
    const isManual = this.retentionPolicyStepLogic.schedule.isManualMode;

    if (isSimple || isManual) {
      event.stop();

      this.modalService
        .open(
          {
            header: { title: this.i18nPipe.transform('wizards:GFSRequiresTitle') },
            footer: { okButton: { text: this.i18nPipe.transform('wizards:GFSRequiresSetScheduleBtn') } }
          },
          this.i18nPipe.transform('wizards:whereBackup.GFSRequiresText')
        )
        .then((result) => {
          if (result) {
            this.retentionPolicyStepLogic.GFSEnabledChangeHandler().then(noop);
          }
        });
    }
  }

  GFSEnabledChangeHandler(): void {
    this.retentionPolicyStepLogic.GFSEnabledChangeHandler().then(noop);
  }

  clickDetailsGFS(needReset?: boolean): void {
    this.retentionPolicyStepLogic.clickDetailsGFS(needReset);
  }

  deleteVersionsOlderThanHybridChangeHandler(state: boolean): void {
    this.retentionPolicyStepLogic.deleteVersionsOlderThanHybridChangeHandler(state);
  }

  toggleFormControls(controlNames: string[], isEnabled: boolean): void {
    if (controlNames.includes('deleteVersionsOlderThanCount') || controlNames.includes('deleteVersionsOlderThanPeriod')) {
      return void super.toggleFormControls(controlNames, isEnabled, this.stepForm.controls.retentionPolicyStep);
    }

    super.toggleFormControls(controlNames, isEnabled);
  }

  protected initFormEvents(): void {
    this.formWrited.pipe(filter(Boolean), take(1), untilDestroyed(this)).subscribe(() => {
      super.initFormEvents();

      this.retentionPolicyStepLogic.initFormEvents();
    });
  }

  private getNewValue(): DestinationPlusRetentionStepValue {
    const whereBackupStep = this.stepForm.controls.whereBackupStep as UntypedFormGroup;
    const retentionPolicyStep = this.stepForm.controls.retentionPolicyStep;

    const isHybrid = this.stepForm.controls.whereBackupStep.controls.IsHybridBackup.value;
    const localStorage = this.stepForm.controls.whereBackupStep.controls.HybridID.value;
    const notLocalStorage = this.stepForm.controls.whereBackupStep.controls.ConnectionID.value;
    const invalidLocalStorage = !localStorage || localStorage === GuidEmpty;
    const invalidNotLocalStorage = !notLocalStorage || notLocalStorage === GuidEmpty;

    const invalidWhereBackup = isHybrid ? invalidLocalStorage || invalidNotLocalStorage : invalidNotLocalStorage;
    const invalidAdvancedOptions = this.advancedOptionsAvailable ? !this.advancedOptionsValid : false;

    return {
      whereBackupStep: { ...whereBackupStep.value, valid: this.stepForm.controls.whereBackupStep.valid },
      retentionPolicyStep: { ...retentionPolicyStep.value, valid: retentionPolicyStep.valid },
      valid: !invalidWhereBackup && retentionPolicyStep.valid && !invalidAdvancedOptions
    };
  }

  private initEvents(): void {
    this.formWrited.pipe(filter(Boolean), take(1), untilDestroyed(this)).subscribe(() => {
      this.retentionPolicyStepLogic.initEvents();
    });

    this.stepService
      .pipeProperty('storage', 'minPeriod')
      .pipe(untilDestroyed(this))
      .subscribe((minPeriod) => (this.minPeriod = minPeriod));
  }

  private changeSelectedHandler(selectedStorage: string[], hybridMode = false): void {
    const firstTable = !hybridMode;

    this.whereBackupStepLogic.changeSelectedHandler(selectedStorage, firstTable);

    if (hybridMode && this.whereBackupStepLogic.selectedLocalStorage) {
      this.stepService.updateProperty('storage', 'hybridName', this.whereBackupStepLogic.selectedLocalStorage.DisplayName);
      this.stepService.updateProperty('storage', 'hybridType', StorageType.LocalFS);
    }
  }

  private getAvailableStorages(): { selected?: StorageConnection; local: Array<StorageConnection>; mix: Array<StorageConnection> } {
    const selected = this.whereBackupStepLogic.selectedNotLocalStorage;
    const local = this.whereBackupStepLogic.LocalStorages.filter((item) => item.ID !== selected?.ID);
    const mix = this.whereBackupStepLogic.MixStorages.filter((item) => item.ID !== selected?.ID);

    return { selected, local, mix };
  }

  private showAddNewDestinationModal(): void {
    this.modalService.openRef(DestinationPlusRetentionStepAddPlanModalComponent, {
      backdrop: true,
      size: MbsSize.lg,
      data: {
        isUnix: this.isUnix,
        isNetBuild: this.isNetBuild,
        hid: this.mainService.hid
      }
    });
  }
}
